import { useState } from "react";
import { Check, Copy, MonitorSmartphone, RefreshCw, ShieldAlert, ShieldCheck, X } from "lucide-react";
import type { PublicDevice } from "../types";
import type { SecurityVerificationState } from "../security";

export type SecurityVerificationData = Readonly<{
  code: string;
  ownDevices: PublicDevice[];
  peerDevices: PublicDevice[];
  state: SecurityVerificationState;
}>;

type Props = Readonly<{
  data: SecurityVerificationData;
  onClose: () => void;
  onRefresh: () => Promise<SecurityVerificationData>;
  onVerify: () => void;
  peerName: string;
}>;

function shortFingerprint(value: string): string {
  return value.length > 24 ? `${value.slice(0, 12)}…${value.slice(-10)}` : value;
}

function DeviceList({ devices }: Readonly<{ devices: readonly PublicDevice[] }>) {
  if (devices.length === 0) return <p className="empty-device-list">Активных устройств не найдено.</p>;
  return <ul className="security-device-list">{devices.map((device) => <li key={device.id}><span className="device-icon"><MonitorSmartphone /></span><span><b>{device.name}</b><code>{shortFingerprint(device.fingerprint)}</code></span></li>)}</ul>;
}

export function SecurityVerificationModal({ data, onClose, onRefresh, onVerify, peerName }: Props) {
  const [current, setCurrent] = useState(data);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      setCurrent(await onRefresh());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось обновить ключи");
    } finally {
      setRefreshing(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(current.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      setError("Не удалось скопировать код");
    }
  };

  const status = current.state === "verified"
    ? { icon: <ShieldCheck />, title: "Код подтверждён", text: "Состав ключей совпадает с последним локальным подтверждением.", className: "verified" }
    : current.state === "changed"
      ? { icon: <ShieldAlert />, title: "Ключи изменились", text: "Состав устройств отличается от ранее подтверждённого кода.", className: "changed" }
      : { icon: <ShieldAlert />, title: "Код ещё не подтверждён", text: "Сравните его с собеседником через другой надёжный канал.", className: "unknown" };

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !refreshing) onClose(); }} role="presentation">
    <section aria-modal="true" className="modal-card security-verification" role="dialog" aria-labelledby="security-verification-title">
      <header><div><p className="eyebrow">БЕЗОПАСНОСТЬ</p><h2 id="security-verification-title">Проверка ключей</h2><small>Личный чат · {peerName}</small></div><button aria-label="Закрыть" className="icon-button" onClick={onClose} type="button"><X /></button></header>
      <div className={`security-verification-status ${status.className}`}><span>{status.icon}</span><span><b>{status.title}</b><small>{status.text}</small></span></div>
      <div className="security-code-box"><span>Код безопасности</span><strong>{current.code}</strong><button aria-label="Скопировать код" className="icon-button" onClick={() => void copyCode()} title="Скопировать код" type="button">{copied ? <Check /> : <Copy />}</button></div>
      <p className="security-verification-copy">Сверьте код и список устройств с собеседником лично или по другому доверенному каналу. Код не отправляется на сервер и хранится только на этом устройстве.</p>
      <div className="security-device-columns"><section><h3>Ваши устройства <small>{current.ownDevices.length}</small></h3><DeviceList devices={current.ownDevices} /></section><section><h3>{peerName} <small>{current.peerDevices.length}</small></h3><DeviceList devices={current.peerDevices} /></section></div>
      {error && <p className="inline-error" role="alert">{error}</p>}
      <footer><button className="text-button" disabled={refreshing} onClick={() => void refresh()} type="button"><RefreshCw className={refreshing ? "spin-icon" : undefined} />Обновить ключи</button><button className="primary-button" disabled={current.state === "verified"} onClick={() => { onVerify(); setCurrent((value) => ({ ...value, state: "verified" })); }} type="button">{current.state === "verified" ? "Код подтверждён" : "Подтвердить этот код"}</button></footer>
    </section>
  </div>;
}
